import * as React from 'react';
import { Mic, Pause, Square, RotateCcw } from 'lucide-react';

import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { useAudioRecording } from 'hooks/use-audio-recording';

export interface AudioRecorderProps {
    onRecordingComplete?: (audioBlob: Blob) => void;
    onCancel?: () => void;
    showControls?: boolean;
    autoStart?: boolean;
    className?: string;
}

const formatDuration = (milliseconds: number): string => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const WaveformVisualization = ({ isRecording }: { isRecording: boolean }) => {
    return (
        <div className="flex h-16 items-center justify-center gap-1">
            {[...Array(20)].map((_, i) => (
                <div
                    key={i}
                    className={cn(
                        'w-1 rounded-full bg-primary transition-all duration-150',
                        isRecording ? 'animate-pulse' : 'h-2 opacity-30',
                    )}
                    style={{
                        height: isRecording
                            ? `${20 + Math.random() * 40}px`
                            : '8px',
                        animationDelay: `${i * 50}ms`,
                    }}
                />
            ))}
        </div>
    );
};

export const AudioRecorder = React.forwardRef<
    HTMLDivElement,
    AudioRecorderProps
>(
    (
        {
            onRecordingComplete,
            onCancel,
            showControls = true,
            autoStart = false,
            className,
        },
        ref,
    ) => {
        const cleanupRef = React.useRef<(() => void) | null>(null);

        const {
            recordingState,
            duration,
            startRecording,
            stopRecording,
            pauseRecording,
            resumeRecording,
            resetRecording,
            isRecording,
            isPaused,
            error,
        } = useAudioRecording({
            onRecordingComplete,
            onError: (err) => {
                console.error('Recording error:', err);
            },
        });

        // Store cleanup function and ensure cleanup on unmount
        React.useEffect(() => {
            cleanupRef.current = resetRecording;

            return () => {
                // Ensure cleanup on unmount
                if (cleanupRef.current) {
                    cleanupRef.current();
                }
            };
        }, [resetRecording]);

        React.useEffect(() => {
            if (autoStart && recordingState === 'idle') {
                startRecording();
            }
        }, [autoStart, recordingState, startRecording]);

        const handleStop = () => {
            stopRecording();
        };

        const handleReset = () => {
            resetRecording();
            if (autoStart) {
                setTimeout(() => startRecording(), 100);
            }
        };

        const handleCancel = () => {
            resetRecording();
            onCancel?.();
        };

        return (
            <div ref={ref} className={cn('space-y-4', className)}>
                {error && (
                    <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                        {error.message}
                    </div>
                )}

                <div className="rounded-lg border bg-muted/30 p-4">
                    <div className="mb-2 text-center">
                        <div className="font-mono text-2xl font-semibold tabular-nums">
                            {formatDuration(duration)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                            {recordingState === 'idle' && 'Ready to record'}
                            {recordingState === 'recording' && 'Recording...'}
                            {recordingState === 'paused' && 'Paused'}
                            {recordingState === 'stopped' &&
                                'Recording complete'}
                        </div>
                    </div>

                    <WaveformVisualization isRecording={isRecording} />
                </div>

                {showControls && (
                    <div className="flex items-center justify-center gap-2">
                        {recordingState === 'idle' && (
                            <>
                                <Button
                                    type="button"
                                    onClick={startRecording}
                                    className="gap-2"
                                >
                                    <Mic className="h-4 w-4" />
                                    Start Recording
                                </Button>
                                {onCancel && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleCancel}
                                    >
                                        Cancel
                                    </Button>
                                )}
                            </>
                        )}

                        {(recordingState === 'recording' ||
                            recordingState === 'paused') && (
                            <>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    onClick={handleReset}
                                    title="Restart"
                                >
                                    <RotateCcw className="h-4 w-4" />
                                </Button>

                                {isRecording && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="icon"
                                        onClick={pauseRecording}
                                        title="Pause"
                                    >
                                        <Pause className="h-4 w-4" />
                                    </Button>
                                )}

                                {isPaused && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={resumeRecording}
                                        className="gap-2"
                                    >
                                        <Mic className="h-4 w-4" />
                                        Resume
                                    </Button>
                                )}

                                <Button
                                    type="button"
                                    onClick={handleStop}
                                    className="gap-2"
                                >
                                    <Square className="h-4 w-4" />
                                    Done
                                </Button>
                            </>
                        )}

                        {recordingState === 'stopped' && (
                            <>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleReset}
                                    className="gap-2"
                                >
                                    <RotateCcw className="h-4 w-4" />
                                    Re-record
                                </Button>
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    },
);

AudioRecorder.displayName = 'AudioRecorder';
